/**
 * Event → sound wiring, kept in one data module so completeness is a type-level and a
 * runtime-testable property rather than something read off a diff.
 *
 * Three trigger surfaces feed `/fly`'s audio, per `docs/engineering/audio-plan.md` and the
 * `EncounterRunner`/`FlightFrame` API in `flight/sim-runner.ts`:
 *
 * 1. **HUD messages** (`FlightFrame.messages`, `readonly string[]`) — the same strings the HUD
 *    prints, sourced from `flight/sim-messages.ts`'s `SIM_EVENT_MESSAGES`/`COMBAT_EVENT_MESSAGES`.
 *    `SIM_EVENT_SFX`/`COMBAT_EVENT_SFX` below are keyed by the *event kind* (typechecked total
 *    over `SimEventKind`/`CombatEventKind` from `@red-opus/engine`) and then flattened by
 *    `messageSfxTable()` into a `Record<message, SfxCueId | null>` for the runtime lookup —
 *    that keeps the authoritative mapping type-safe while the hot path is a plain string lookup
 *    against exactly what `FlightFrame.messages` contains.
 * 2. **Continuous sim state**, read every frame directly off `SimState`/`CombatHud` rather than
 *    from an event: engine (throttle, engine health), wind (airspeed), fire (burning), stall rush
 *    (`readout.stalled`). See `continuous-params.ts` for the gain/loop-region math.
 * 3. **Held input**, read every frame off `FlightInputs.firing` (the `F` key) — gunfire is not an
 *    event at all, it is "is the trigger down right now." Two layers hang off it: rounds actually
 *    leaving the guns (`GUN_SHOT_CUE`) and the dead-trigger click for when they are not
 *    (`DEAD_TRIGGER_CUE`).
 *
 * A `null` entry is a *deliberate* "no additional cue" — e.g. `onFire` starts the continuous
 * fire layer instead of firing a one-shot, and `encounterWon` shares the same tick as
 * `destroyed`'s explosion, so it doesn't need a second bang. `null` is not "not decided yet"; the
 * completeness tests below only require every key to be *present*, not non-null.
 *
 * **Rebaked (2026-08-19, AdLib SFX lane):** `gunJamPermanent` moved off SIMSNDS-16 — measurement
 * (`sound-music.md`) confirms 16 is the genuine gun-rattle cue, so it belongs to gunfire now.
 * `gunJamPermanent` took SIMSNDS-10 instead (a slow loop-marked swell, previously mis-wired as a
 * second gunfire variant on a duration guess that didn't survive the rebake — see its entry in
 * `simsnds-cues.ts`). Every other message mapping is unchanged; only the numbers backing them
 * (durations, loop flags) moved.
 *
 * **Corrected (2026-08-19, ENGINE SOUND lane):** two baked cues lost their roles outright to the
 * live OPL capture (`docs/formats/sound-music.md` § "Continuous engine sound"), and neither was
 * replaced by another baked cue:
 * - **Cue 2 is the player's engine, not a gun burst.** It is the one sound the sim starts before
 *   anything else, it is never released, and it is the only voice whose pitch tracks the throttle.
 *   It is no longer a baked buffer at all here: `engine-voice.ts` runs its patch live on a real
 *   OPL2 voice in an `AudioWorklet`, which is the only way to render the pitch glide.
 * - **Cue 0 was never played in any of the four captured flights**, so the "engine catches" accent
 *   it used to trigger is gone. There is no `ENGINE_ACCENT_CUE` any more; the original has no
 *   engine-start sound to be faithful to.
 *
 * **Corrected (2026-08-21, GUN AUDIO lane)** — the first capture ever taken with the guns firing
 * (`docs/formats/sound-music.md` § "Gunfire, jams and hits", session `re-sessions/gun-audio/`)
 * replaced guesswork with measurement on five roles:
 *
 * | Role | Was | Now | Basis |
 * |---|---|---|---|
 * | player's gunfire | SIMSNDS-16 @ 0.383 s | **SIMSNDS-01 @ 0.1183 s** | VERIFIED — cue 1, program 3, one key-on per round |
 * | trigger down, nothing firing | (silent) | **SIMSNDS-11** clusters | VERIFIED — cue 11, program 14, jam *and* empty |
 * | `gunJammed` | SIMSNDS-12 | **SIMSNDS-11** | VERIFIED — the first click cluster is the announcement |
 * | `gunCleared` | SIMSNDS-29 | **SIMSNDS-12** | VERIFIED — cue 12 is what the `U` keypress plays |
 * | `takingHits` / flak hit | SIMSNDS-04 | **SIMSNDS-13** | VERIFIED — cue 13 fires as rounds strike you |
 *
 * SIMSNDS-04 is the *enemy's* gunfire, reserved rather than reused by that pass and **wired
 * 2026-08-27** (G36 — `ENEMY_GUN_CUE`, played by `enemy-gun-audio.ts`); SIMSNDS-16 was never
 * played by the original at all and is downgraded to an ordinary INFERRED slot. The three displaced INFERRED roles (`carburettorThawed`,
 * `pilotWounded`, `bleedOutWarning`) moved onto the slots this freed — see their entries below.
 */

import type { CombatEventKind, SimEventKind } from "@red-opus/engine";
import {
  COMBAT_EVENT_MESSAGES,
  DANGEROUSLY_LOW_MESSAGE,
  FLAK_HIT_MESSAGE,
  LOW_FUEL_MESSAGE,
  SIM_EVENT_MESSAGES,
} from "../flight/sim-messages.ts";
import { OOPS, SCRATCH_ONE, SHOT_DOWN, ZEPPELIN_DESTROYED } from "../flight/sim-callouts.ts";
import type { SfxCueId } from "./simsnds-cues.ts";

/** One id per `SimEventKind` — see `simsnds-cues.ts` for the per-cue evidence/reasoning. */
export const SIM_EVENT_SFX: Readonly<Record<SimEventKind, SfxCueId | null>> = {
  stallEntered: "SIMSNDS-20",
  stallRecovered: "SIMSNDS-14",
  spinEntered: "SIMSNDS-18",
  spinRecovered: "SIMSNDS-15",
  spiralEntered: "SIMSNDS-05",
  wingFailure: "SIMSNDS-08",
  // VERIFIED (GUN AUDIO lane): the jam announces itself with the first dead-trigger
  // click cluster, cue 11 — the same cue `DEAD_TRIGGER_CUE` keeps playing while the
  // trigger stays down. The capture shows no separate jam-onset sting at all.
  gunJammed: "SIMSNDS-11",
  gunJamPermanent: "SIMSNDS-10",
  // VERIFIED: pressing `U` plays cue 12 (program 15), one 320 ms note. In the captured
  // attempt the jam did *not* clear, so cue 12 is the clear-jam *action*, not a
  // confirmed "cleared" — the port fires it on the success event because that is the
  // only clear-jam hook the audio layer is given. See sound-music.md's open item.
  gunCleared: "SIMSNDS-12",
  carburettorIced: "SIMSNDS-28",
  // Moved off SIMSNDS-01 (now VERIFIED gunfire). SIMSNDS-29 is the other
  // shell-blip byte-twin, so this keeps exactly the character the old entry
  // claimed — reused notification audio — on a slot cue 12 just freed. INFERRED.
  carburettorThawed: "SIMSNDS-29",
  fuelExhausted: "SIMSNDS-22",
  hypoxiaDizzy: "SIMSNDS-27",
  hypoxiaBlackout: "SIMSNDS-07",
  hypoxiaRecovered: "SIMSNDS-21",
  // Moved off SIMSNDS-11 (now VERIFIED dead-trigger click) onto the slot cue 16 just
  // vacated: a wound announcement must not sound like the click you hear every time
  // you pull a jammed trigger. INFERRED.
  pilotWounded: "SIMSNDS-16",
  // Shares SIMSNDS-13 with `takingHits` now that 13 is VERIFIED as the round-strike
  // cue: both lines mean "you are being damaged", and the shipped cue pool has no
  // spare left after this lane's four verified reassignments. INFERRED.
  bleedOutWarning: "SIMSNDS-13",
  pilotDied: "SIMSNDS-09",
  groundContact: "SIMSNDS-17",
  // The undercarriage events have no cue of their own: the SIMSNDS census
  // assigned every one of the 34 baked cues a role from the captured flights,
  // and none of those flights ever took off or landed. Guessing one here would
  // put unearned provenance on a cue that already means something else — so a
  // gear collapse is heard through `groundContact`'s arrival cue plus the
  // message line, and a lift-off and a rollout are silent.
  liftedOff: null,
  gearCollapsed: null,
  landed: null,
  crashed: "SIMSNDS-06",
};

/**
 * One entry per `CombatEventKind`, `own`/`other` split the same way
 * `sim-messages.ts`'s `COMBAT_EVENT_MESSAGES` is — a cue only plays from the side that gets a
 * message at all (`messageSfxTable()` below only ever looks up strings that were actually
 * produced, so the `null` "other"/"own" half of a one-sided event is inert, not a gap).
 */
interface CombatSfxPhrasing {
  readonly own: SfxCueId | null;
  readonly other: SfxCueId | null;
}

export const COMBAT_EVENT_SFX: Readonly<Record<CombatEventKind, CombatSfxPhrasing>> = {
  roundsOnTarget: { own: "SIMSNDS-03", other: null },
  // VERIFIED (GUN AUDIO lane): rounds striking your aeroplane are cue 13 (program 16),
  // 219 ms notes on their own voices at 490-710 ms intervals, captured while von
  // Richthofen shot the player down. SIMSNDS-04, which used to hold this role, is the
  // *enemy's gunfire* — see `ENEMY_GUN_CUE`.
  takingHits: { own: "SIMSNDS-13", other: null },
  engineSputtering: { own: "SIMSNDS-22", other: null },
  onFire: { own: null, other: null },
  outOfControl: { own: null, other: "SIMSNDS-07" },
  destroyed: { own: null, other: "SIMSNDS-06" },
  encounterWon: { own: null, other: null },
  encounterLost: { own: "SIMSNDS-09", other: null },
};

/**
 * The lines `sim-messages.ts` produces outside the event feeds: the two
 * threshold warnings, and archie.
 *
 * A flak hit reuses `takingHits`'s own strike cue rather than getting one of
 * its own, and that is a deliberate limit rather than an oversight: the shipped
 * `SIMSNDS` set has no spare entry (`audio-plan.md`'s roster is fully routed
 * after the 2026-08-19 rebake correction), and `aa.c` plays its burst through
 * the same sound entry point as everything else with an id this lane did not
 * decode. Reusing the strike tick is honest; inventing a boom would not be.
 */
export const DERIVED_MESSAGE_SFX: Readonly<Record<string, SfxCueId | null>> = {
  [LOW_FUEL_MESSAGE]: "SIMSNDS-22",
  [DANGEROUSLY_LOW_MESSAGE]: "SIMSNDS-20",
  // Follows `takingHits` onto the VERIFIED round-strike cue; the reasoning in the
  // docstring above (flak reuses the strike tick rather than inventing a boom) is
  // unchanged, only the tick's id moved.
  [FLAK_HIT_MESSAGE]: "SIMSNDS-13",
};

/**
 * Two distinct `CombatEventKind`s can render to the *identical* string — `destroyed`'s "other"
 * side and `encounterWon`'s "own" side both read "ENEMY DESTROYED" (same moment, two cockpits'
 * worth of `CombatEvent`s on the same tick). Message-text keying can't tell those apart, so a
 * cue assigned by either kind must survive a `null` written by the other — first non-null wins,
 * independent of which kind happens to iterate first.
 */
function setMessageCue(table: Record<string, SfxCueId | null>, message: string, cue: SfxCueId | null): void {
  const existing = table[message];
  table[message] = existing === undefined ? cue : (existing ?? cue);
}

/**
 * Flattens the three tables above into one `HUD message text → cue id` lookup — the shape
 * `FlightFrame.messages` (and the audio engine's `handleMessages`) actually consumes. Built once
 * per import, not per frame.
 */
function buildMessageSfxTable(): Readonly<Record<string, SfxCueId | null>> {
  const table: Record<string, SfxCueId | null> = { ...DERIVED_MESSAGE_SFX };
  for (const [kind, message] of Object.entries(SIM_EVENT_MESSAGES)) {
    setMessageCue(table, message, SIM_EVENT_SFX[kind as SimEventKind]);
  }
  for (const [kind, phrasing] of Object.entries(COMBAT_EVENT_MESSAGES)) {
    const cues = COMBAT_EVENT_SFX[kind as CombatEventKind];
    if (phrasing.own !== null) setMessageCue(table, phrasing.own, cues.own);
    if (phrasing.other !== null) setMessageCue(table, phrasing.other, cues.other);
  }
  return table;
}

export const MESSAGE_SFX_TABLE: Readonly<Record<string, SfxCueId | null>> = buildMessageSfxTable();

/**
 * The kill lines G33 restored are `printf` prefixes with the victim's name
 * appended (`Scratch one balloon`, `You just shot down German Fokker Dr.I`), so
 * no exact-match table can hold them. They are the *same event* the flat
 * `ENEMY DESTROYED` used to announce — and they replace it in the feed — so they
 * take its cue rather than a new one, and the explosion sounds exactly as it did
 * before the line learned the aeroplane's name.
 *
 * Prefixes only, checked after the exact table misses: a full-string table would
 * have to enumerate 33 aeroplanes × 3 nationalities.
 */
const MESSAGE_PREFIX_SFX: readonly (readonly [string, SfxCueId | null])[] = [
  [SCRATCH_ONE, COMBAT_EVENT_SFX.destroyed.other],
  [SHOT_DOWN, COMBAT_EVENT_SFX.destroyed.other],
  [OOPS, COMBAT_EVENT_SFX.destroyed.other],
  [ZEPPELIN_DESTROYED, COMBAT_EVENT_SFX.destroyed.other],
];

/** The cue for one HUD message line, or `undefined` if the string isn't one this table knows (or `null` for a deliberate no-cue entry). */
export function sfxForMessage(message: string): SfxCueId | null | undefined {
  const exact = MESSAGE_SFX_TABLE[message];
  if (exact !== undefined) return exact;
  return MESSAGE_PREFIX_SFX.find(([prefix]) => message.startsWith(prefix))?.[1];
}

/**
 * Continuous / held-input cues, not event-driven — see `continuous-params.ts` for the gain/loop
 * curves that drive these every frame.
 */

/** Looped wind bed (not a loop-marked cue — see its entry in `simsnds-cues.ts`); `gain`/`playbackRate` track airspeed. */
export const WIND_LOOP_CUE: SfxCueId = "SIMSNDS-25";

/**
 * **VERIFIED** (2026-08-21, GUN AUDIO lane) — the player's machine guns.
 *
 * A live OPL2 register capture of `RB.EXE` firing (`docs/formats/sound-music.md`
 * § "Gunfire, jams and hits", session `re-sessions/gun-audio/`) settles the open
 * item this constant used to carry. The original plays **`SIMSNDS` cue 1**, bank
 * program 3, MIDI channel 10 note 14, on **one** OPL voice — keyed on for 101 ms,
 * off for 17 ms, keyed on again, **once per round**, at a period of
 * `GUN_SHOT_INTERVAL_S`. There is no rattle cue, no second layer, no trigger-down
 * accent, and no per-shot variation: every shot writes the identical registers.
 *
 * This retires `GUN_TAP_CUE = "SIMSNDS-16"`. Cue 16 (program 23) was **never
 * played once** in any of the six captured flights — not by the player's guns,
 * not by the enemy's, not on a hit. Playing its 0.785 s nine-note rattle every
 * 0.383 s was the user-reported defect ("the machine gun sounds are definitely
 * off"): a hissy 9.4 kHz-centroid burst where the original has a low 725 Hz crack.
 *
 * Cue 1's baked render is *already correct* — the DOSBox capture replayed through
 * `packages/baker`'s OPL2 correlates 0.959 with `data/baked/sfx/SIMSNDS-01.ogg`
 * and matches it to within 0.7 dB in every octave band — so no rebake is needed,
 * only this wiring.
 */
export const GUN_SHOT_CUE: SfxCueId = "SIMSNDS-01";

/**
 * **VERIFIED** — what the original plays when the trigger is down and nothing
 * comes out, whether the guns are jammed or the belt is empty. Both states use the
 * same cue: `SIMSNDS` cue 11, bank program 14, in the clusters
 * `continuous-params.ts`'s `JAMMED_TRIGGER_CLUSTER` / `EMPTY_TRIGGER_CLUSTER`
 * measure. There is no separate jam-onset sting — the first cluster *is* the
 * announcement, landing on the same frame as "Your guns have jammed." /
 * "You are out of ammunition."
 */
export const DEAD_TRIGGER_CUE: SfxCueId = "SIMSNDS-11";

/**
 * **VERIFIED** — the *enemy's* guns are `SIMSNDS` cue 4 (program 7), fired on the
 * same 118 ms cadence as the player's but with a carrier total level that tracks
 * range (9 → 18 as the Fokker fell back), i.e. distance-attenuated exactly like
 * the enemy-engine voice `engine-voice.ts` models. (This is why `takingHits`
 * moved off SIMSNDS-04.)
 *
 * **Wired 2026-08-27 (G36).** It was reserved-but-unplayed for six days because
 * the port had no "some other aeroplane is firing" signal to hang it on —
 * `ContinuousFrameParams` carried a nearest-enemy *range* but not a nearest-enemy
 * *trigger*. `encounter-views.ts`'s `AircraftView.firingNow` is that trigger, and
 * `enemy-gun-audio.ts` is the voice pool; the constant lost its `_UNWIRED` suffix
 * with the wiring.
 *
 * **Widened 2026-09-01 to the guns that are not on an aeroplane at all.** A
 * Zeppelin's four stations and an escort sloop's two Maxims shoot at the player
 * and had never made a sound, because `firingNow` is an `AircraftView` field and
 * neither of them is on `state.aircraft`. `frame-signals.ts`'s
 * `firingBatteryRangesM` is the second trigger and the two arrive at the pool as
 * one list. They get **this** cue at **this** level rather than one of their
 * own: the bank holds exactly two gun cues, yours and somebody else's, and a
 * Maxim in a splinter tub is somebody else's. See `ship-sim.md` §4.2b for the
 * measured levels.
 */
export const ENEMY_GUN_CUE: SfxCueId = "SIMSNDS-04";

/** Looped, loop-marked continuous fire bed; gain ramps by whether the aeroplane is burning (`FIRE_LOOP_RAMP_S`). */
export const FIRE_LOOP_CUE: SfxCueId = "SIMSNDS-19";
/** Retriggered at randomized intervals on top of `FIRE_LOOP_CUE` while burning, cycled round-robin for pop-to-pop variety. */
export const FIRE_CRACKLE_POP_CUES: readonly SfxCueId[] = ["SIMSNDS-23", "SIMSNDS-26"];

/** Looped, loop-marked stall-buffet bed; gain ramps by `SimState.readout.stalled` (`STALL_RUSH_RAMP_S`). */
export const STALL_RUSH_LOOP_CUE: SfxCueId = "SIMSNDS-24";

/**
 * **INFERRED, not verified** — the ambient "archie" crump for a shell going off near the player,
 * distance-attenuated by `continuous-params.ts`'s `flakBurstGain` and keyed off the same per-tick
 * `FlakBurst` list the visuals draw a sprite from (`flight/encounter-views.ts`'s `effectSpawnsOf`).
 * This is a different event from a flak *hit*: `DERIVED_MESSAGE_SFX`'s `FLAK_HIT_MESSAGE` entry
 * already plays `SIMSNDS-13` for a shell that actually damages you, reusing the VERIFIED
 * round-strike cue; this cue is for every burst the visuals show, hit or miss, which is most of
 * them — `docs/engineering/flak-design.md` §9 measures 93.6% of archie as noise.
 *
 * No live capture pins a distinct "flak burst" audio cue — `AA.DAT` is a data table (geometry and
 * timers only), and the two OPL sessions behind `docs/formats/sound-music.md` never captured a
 * flak engagement. Absent that evidence, this reuses **`SIMSNDS-06`**, the catalog's only cue with
 * an established explosion/impact character (`simsnds-catalog.ts`: "terminal-impact tone", already
 * shared by `crashed` and `destroyed`) — an anti-aircraft shell detonating is squarely the same
 * sonic category, and reusing a documented "boom" honestly beats inventing an unrelated one. Per
 * the lane brief this deliberately avoids both gunfire cues (`GUN_SHOT_CUE`, `SIMSNDS-01`, and
 * `ENEMY_GUN_CUE`, `SIMSNDS-04`) — flak is not gunfire, and neither cue's established
 * character (a crack, not a boom) fits it anyway.
 */
export const FLAK_BURST_CUE: SfxCueId = "SIMSNDS-06";
